/**
 * TransferList Component
 *
 * A flexible component for transferring items between two lists with support for:
 * - String or custom object items
 * - Search/filtering
 * - Bulk transfer operations
 * - React Hook Form integration
 * - Loading states
 * - Table and list variants
 * - Responsive mobile layouts
 * - Pagination for large datasets
 * - Conditional column visibility
 *
 * @example
 * // Basic usage with strings
 * <InputTransferList
 *   initialData={{ left: ['Item 1', 'Item 2'], right: [] }}
 *   onChange={(data) => console.log(data)}
 * />
 *
 * @example
 * // With custom objects
 * <InputTransferList
 *   initialData={{ left: users, right: [] }}
 *   getItemLabel={(user) => user.name}
 *   getItemValue={(user) => user.id}
 * />
 *
 * @example
 * // With React Hook Form
 * <Input.TransferList
 *   name="assignedUsers"
 *   control={control}
 *   rules={{ required: true }}
 *   getItemLabel={(user) => user.name}
 *   getItemValue={(user) => user.id}
 * />
 *
 * @example
 * // With loading state
 * <InputTransferList
 *   value={data}
 *   onChange={setData}
 *   loading={isLoading}
 *   loaderProps={{ color: 'blue', variant: 'dots' }}
 * />
 *
 * @example
 * // With conditional column visibility
 * <InputTransferList
 *   variant="table"
 *   columns={[
 *     { key: 'name', label: 'Name' },
 *     {
 *       key: 'actions',
 *       label: 'Actions',
 *       hide: ({ side }) => side === 'right', // Only show on left list
 *       render: (_, item) => <Button>Edit</Button>
 *     }
 *   ]}
 * />
 *
 * @example
 * // With pagination (basic)
 * <InputTransferList
 *   initialData={{ left: largeDataset, right: [] }}
 *   pagination={true} // Enables pagination with default settings (10 items per page)
 * />
 *
 * @example
 * // With pagination (custom configuration)
 * <InputTransferList
 *   initialData={{ left: largeDataset, right: [] }}
 *   pagination={{
 *     pageSize: 20,
 *     pageSizeOptions: [10, 20, 50, 100],
 *     onPageChange: (list, page, pageSize) => {
 *       console.log(`${list} list: page ${page}, size ${pageSize}`);
 *     }
 *   }}
 * />
 *
 * @example
 * // With pagination (separate configuration for each list)
 * <InputTransferList
 *   initialData={{ left: largeDataset, right: [] }}
 *   pagination={{
 *     left: { pageSize: 10, pageSizeOptions: [5, 10, 20] },
 *     right: { pageSize: 20, pageSizeOptions: [10, 20, 50] }
 *   }}
 * />
 *
 * @example
 * // Programmatic pagination control via ref
 * const ref = useRef<TransferListRef>(null);
 *
 * // Navigate to specific page
 * ref.current?.goToPage('left', 3);
 *
 * // Change page size
 * ref.current?.setPageSize('right', 50);
 *
 * // Get pagination state
 * const state = ref.current?.getPaginationState();
 * console.log(state.left); // { currentPage: 1, pageSize: 10, totalItems: 100, totalPages: 10 }
 *
 * Value Format for React Hook Form:
 * The component expects and returns data in the following format:
 * {
 *   left: T[],   // Array of items in the left list
 *   right: T[]   // Array of items in the right list
 * }
 *
 * Where T can be:
 * - string (e.g., 'Item 1', 'Item 2')
 * - Custom object (e.g., { id: 1, name: 'John' })
 *
 * Pagination:
 * The pagination prop supports three formats:
 * 1. false (default): Pagination disabled
 * 2. true: Pagination enabled with default settings (10 items per page)
 * 3. Configuration object: Custom pagination settings
 * 4. Separate configuration: Different settings for left and right lists
 *
 * Pagination works seamlessly with search - when searching, pagination is applied
 * to filtered results and automatically resets to page 1 when search changes.
 *
 * Backward Compatibility:
 * The pagination feature is completely optional and does not break existing implementations.
 * Components without the pagination prop will continue to work exactly as before.
 */

import { ActionIcon, Checkbox, Combobox, Group, Loader, Text, TextInput, Tooltip, useCombobox } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronRight, IconChevronsRight } from "@tabler/icons-react";
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { cn } from "@/utils/cn";

import { PaginationControls } from "./PaginationControls";
import { useTransferListPagination } from "./TransferList.hooks";
import classes from "./TransferList.module.css";
import type { InputTransferListProps, RenderListProps, TransferListColumn, TransferListData, TransferListItem, TransferListRef } from "./TransferList.types";
import { defaultFilter, defaultLabels, resolvePaginationConfig, resolveTooltipLabel, resolveTooltipProps } from "./TransferList.utils";

// Loader size mapping to convert component size to Loader size
const loaderSizeMap = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl"
} as const;

function RenderList<T = TransferListItem>({
  options,
  selectedValues,
  onSelectionChange,
  onTransfer,
  onTransferAll,
  type,
  getItemLabel,
  getItemValue,
  searchable,
  showTransferAll,
  disabled,
  canTransfer,
  filter,
  searchPlaceholder,
  emptyMessage,
  nothingFoundMessage,
  height,
  variant,
  columns,
  classNames,
  orientation,
  size,
  loading = false,
  loaderProps,
  paginationConfig,
  listSide,
  paginationRef,
  tooltipConfig,
  color
}: RenderListProps<T>) {
  const combobox = useCombobox();
  const [search, setSearch] = useState("");

  // Handle item selection toggle
  const handleValueSelect = (itemValue: string | number) => {
    const newSelection = selectedValues.includes(itemValue) ? selectedValues.filter((v) => v !== itemValue) : [...selectedValues, itemValue];
    onSelectionChange(newSelection);
  };

  /**
   * Extract cell value from an item for a specific column
   * Uses valueGetter if provided, otherwise falls back to direct property access
   * Handles errors gracefully by logging warnings and returning empty string
   *
   * @param item - The item to extract value from
   * @param column - The column definition
   * @returns The extracted value or empty string on error
   */
  const getCellValue = (item: T, column: TransferListColumn<T>): unknown => {
    try {
      // Use valueGetter if provided
      if (column.valueGetter) {
        const value = column.valueGetter(item);
        // Handle null/undefined by returning empty string
        return value ?? "";
      }

      // Fall back to direct property access
      const value = (item as Record<string, unknown>)[column.key];
      // Handle null/undefined by returning empty string
      return value ?? "";
    } catch (error) {
      // Log warning for debugging
      console.warn(`Error extracting value for column "${column.key}":`, error);
      // Return empty string as fallback
      return "";
    }
  };

  // Filter columns based on hide property
  const visibleColumns = useMemo(() => {
    if (!columns) return [];

    return columns.filter((column) => {
      if (column.hide === undefined) return true;
      if (typeof column.hide === "boolean") return !column.hide;
      return !column.hide({ side: listSide });
    });
  }, [columns, listSide]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchable || !search) {
      return options;
    }

    // Use custom filter if provided
    if (filter) {
      return options.filter((item) => filter(item, search));
    }

    // For table variant, search across all visible columns
    if (variant === "table" && visibleColumns && visibleColumns.length > 0) {
      const searchLower = search.toLowerCase().trim();
      return options.filter((item) => {
        // Search in all visible column values using getCellValue helper
        return visibleColumns.some((column) => {
          // Extract value using getCellValue (handles valueGetter and errors)
          const cellValue = getCellValue(item, column);

          // Convert to string for search comparison
          const cellValueStr = String(cellValue);

          return cellValueStr.toLowerCase().includes(searchLower);
        });
      });
    }

    // For list variant, use default filter with getItemLabel
    return options.filter((item) => defaultFilter(item, search, getItemLabel));
  }, [options, search, searchable, filter, getItemLabel, variant, visibleColumns]);

  // Apply pagination to filtered items
  const pagination = useTransferListPagination(filteredItems, paginationConfig ?? null, listSide);

  // Store pagination hook state in ref for imperative control
  if (paginationRef) {
    paginationRef.current = pagination;
  }

  // Use paginated items if pagination is enabled, otherwise use all filtered items
  const itemsToRender = paginationConfig ? pagination.paginatedItems : filteredItems;

  // Handle transfer of selected items
  const handleTransferSelected = () => {
    const itemsToTransfer = options.filter((item) => selectedValues.includes(getItemValue(item)));
    onTransfer(itemsToTransfer);
    onSelectionChange([]);
  };

  // Handle transfer of all filtered items
  const handleTransferAllClick = () => {
    // Pass filtered items to parent's handleTransferAll which will handle validation and callbacks
    onTransferAll(filteredItems);
    // Clear selection after transfer
    onSelectionChange([]);
  };

  /**
   * Selection Logic with Pagination
   *
   * The selection system is designed to work seamlessly across pages:
   *
   * 1. Selection Storage: Uses unique item values (via getItemValue) instead of indices,
   *    ensuring selections persist when navigating between pages.
   *
   * 2. Page-Level Selection: The "Select All" checkbox only affects items on the current page,
   *    preserving selections from other pages.
   *
   * 3. Cross-Page Selection: Users can select items on page 1, navigate to page 2, select more items,
   *    and all selections are maintained. When transferring, all selected items from all pages are moved.
   *
   * 4. Visual Feedback: Items remain visually checked when returning to a page where they were selected.
   *
   * 5. Transfer Behavior: handleTransferSelected uses the full 'options' array (not just current page),
   *    ensuring all selected items across all pages are transferred together.
   */

  // Check if all items on current page are selected
  const allPageItemsSelected = itemsToRender.length > 0 && itemsToRender.every((item) => selectedValues.includes(getItemValue(item)));

  // Check if some (but not all) items on current page are selected
  const somePageItemsSelected = itemsToRender.some((item) => selectedValues.includes(getItemValue(item))) && !allPageItemsSelected;

  // Handle "Select All" for current page only
  const handleSelectAllInPage = () => {
    const pageItemValues = itemsToRender.map(getItemValue);

    if (allPageItemsSelected) {
      // Deselect all items on current page (keep selections from other pages)
      onSelectionChange(selectedValues.filter((v) => !pageItemValues.includes(v)));
    } else {
      // Select all items on current page (merge with existing selections from other pages)
      const newSelection = [...selectedValues.filter((v) => !pageItemValues.includes(v)), ...pageItemValues];
      onSelectionChange(newSelection);
    }
  };

  // Render list items based on variant
  const renderListItems = () => {
    if (variant === "table" && visibleColumns && visibleColumns.length > 0) {
      // Table variant rendering
      return (
        <div className={cn(classes.tableContainer, classNames?.table)}>
          <table className={classes.table}>
            <thead className={cn(classes.tableHeader, classNames?.tableHeader)}>
              <tr>
                <th className={classes.tableCheckboxCell}>
                  <Checkbox size={size} checked={allPageItemsSelected} indeterminate={somePageItemsSelected} onChange={handleSelectAllInPage} disabled={disabled} />
                </th>
                {visibleColumns.map((column, index) => (
                  <th
                    key={`${column.key}-${index}`}
                    className={cn(classes.tableHeaderCell, classNames?.tableCell)}
                    style={{
                      width: column.width,
                      textAlign: column.headerAlign || column.align || "left"
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {itemsToRender.map((item) => {
                const itemValue = getItemValue(item);
                const isSelected = selectedValues.includes(itemValue);

                return (
                  <tr
                    key={String(itemValue)}
                    className={cn(classes.tableRow, isSelected && classes.tableRowSelected, classNames?.tableRow)}
                    onClick={() => !disabled && handleValueSelect(itemValue)}
                    data-disabled={disabled}
                  >
                    <td className={classes.tableCheckboxCell}>
                      <Checkbox size={size} checked={isSelected} onChange={() => {}} aria-hidden tabIndex={-1} style={{ pointerEvents: "none" }} disabled={disabled} />
                    </td>
                    {visibleColumns.map((column, colIndex) => {
                      // Extract value using getCellValue helper
                      const cellValue = getCellValue(item, column);

                      // Render cell content with error handling
                      let cellContent: React.ReactNode;
                      try {
                        if (column.render) {
                          // Call render function with value and item (new signature)
                          // Backward compatibility: render functions can accept either (item) or (value, item)
                          // The new signature passes value as first parameter and item as second
                          const rendered = column.render(cellValue, item);
                          // Handle null/undefined by returning empty string
                          cellContent = rendered ?? "";
                        } else {
                          // Default: convert value to string
                          cellContent = String(cellValue);
                        }
                      } catch (error) {
                        // Log warning for debugging
                        console.warn(`Error rendering column "${column.key}":`, error);
                        // Return empty string as fallback
                        cellContent = "";
                      }

                      return (
                        <td
                          key={`${itemValue}-${column.key}-${colIndex}`}
                          className={cn(classes.tableCell, classNames?.tableCell)}
                          style={{
                            textAlign: column.align || "left"
                          }}
                        >
                          {cellContent}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {itemsToRender.length === 0 && <div className={classes.emptyState}>{search ? nothingFoundMessage : emptyMessage}</div>}
        </div>
      );
    }

    // Default list variant rendering
    return itemsToRender.map((item) => {
      const itemValue = getItemValue(item);
      const itemLabel = getItemLabel(item);
      const isSelected = selectedValues.includes(itemValue);

      return (
        <Combobox.Option value={String(itemValue)} key={String(itemValue)} active={isSelected} onMouseOver={() => combobox.resetSelectedOption()} disabled={disabled}>
          <Group gap="sm">
            <Checkbox size={size} checked={isSelected} onChange={() => {}} aria-hidden tabIndex={-1} style={{ pointerEvents: "none" }} disabled={disabled} />
            <span>{itemLabel}</span>
          </Group>
        </Combobox.Option>
      );
    });
  };

  return (
    <div className={cn(classes.renderList, classNames?.renderList)} data-type={type} data-disabled={disabled || loading} data-orientation={orientation} data-size={size}>
      <Combobox store={combobox} onOptionSubmit={(val) => handleValueSelect(val)}>
        <Combobox.EventsTarget>
          <Group wrap="nowrap" gap={0} className={cn(classes.controls, classNames?.controls)}>
            {searchable && (
              <TextInput
                placeholder={searchPlaceholder}
                classNames={{ input: cn(classes.input, classNames?.input), root: cn(classes.inputWrapper, classNames?.inputWrapper) }}
                value={search}
                variant="default"
                size={size}
                radius="xs"
                onChange={(event) => {
                  setSearch(event.currentTarget.value);
                  combobox.updateSelectedOptionIndex();
                }}
                disabled={disabled || loading}
                color={color}
              />
            )}
            {showTransferAll && (
              <Tooltip
                label={resolveTooltipLabel(
                  "all",
                  listSide,
                  tooltipConfig,
                  disabled || loading || filteredItems.length === 0 || !canTransfer,
                  !canTransfer ? "Limite de itens atingido" : filteredItems.length === 0 ? "Nenhum item para transferir" : loading ? "Carregando..." : undefined
                )}
                {...resolveTooltipProps(listSide, tooltipConfig)}
              >
                <ActionIcon
                  radius={0}
                  variant="default"
                  size={size}
                  className={cn(classes.controlTransferAll, classNames?.controlTransferAll)}
                  onClick={handleTransferAllClick}
                  disabled={disabled || loading || filteredItems.length === 0 || !canTransfer}
                  data-max-items={!canTransfer ? "true" : undefined}
                >
                  <IconChevronsRight className={classes.icon} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip
              label={resolveTooltipLabel(
                "selected",
                listSide,
                tooltipConfig,
                disabled || loading || selectedValues.length === 0 || !canTransfer,
                !canTransfer ? "Limite de itens atingido" : selectedValues.length === 0 ? "Nenhum item selecionado" : loading ? "Carregando..." : undefined
              )}
              {...resolveTooltipProps(listSide, tooltipConfig)}
            >
              <ActionIcon
                radius={0}
                variant="default"
                size={size}
                className={cn(classes.control, classNames?.control)}
                onClick={handleTransferSelected}
                disabled={disabled || loading || selectedValues.length === 0 || !canTransfer}
                data-max-items={!canTransfer ? "true" : undefined}
              >
                <IconChevronRight className={classes.icon} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Combobox.EventsTarget>

        <div className={cn(classes.list, classNames?.list)} style={height ? { height } : undefined} data-variant={variant}>
          {loading ? (
            <div className={classes.loaderContainer}>
              <Loader size={loaderSizeMap[size]} {...loaderProps} />
            </div>
          ) : variant === "table" ? (
            renderListItems()
          ) : (
            <Combobox.Options>{itemsToRender.length > 0 ? renderListItems() : <Combobox.Empty>{search ? nothingFoundMessage : emptyMessage}</Combobox.Empty>}</Combobox.Options>
          )}
        </div>

        {/* Pagination controls */}
        {paginationConfig && <PaginationControls pagination={pagination} config={paginationConfig} disabled={disabled || loading} size={size} color={color} />}
      </Combobox>
    </div>
  );
}

function InputTransferListInner<T = TransferListItem>(
  {
    initialData,
    value,
    onChange,
    onTransfer,
    onTransferAll,
    onBeforeTransfer,
    onDuplicateDetected,
    maxItems,
    getItemLabel: customGetItemLabel,
    getItemValue: customGetItemValue,
    labels,
    leftTitle,
    rightTitle,
    tooltips,
    searchable = true,
    showTransferAll = true,
    disabled = false,
    filter,
    height,
    className,
    classNames,
    variant = "list",
    columns,
    orientation = "horizontal",
    mobile = { verticalOrientation: true, breakpoint: 768 },
    size = "md",
    loading = false,
    loaderProps,
    error,
    name: _name,
    onBlur,
    pagination,
    allowDuplicates = false,
    color
  }: InputTransferListProps<T>,
  ref: React.Ref<TransferListRef<T>>
) {
  // Mobile detection using useMediaQuery
  const isMobile = useMediaQuery(`(max-width: ${mobile.breakpoint || 768}px)`);

  // Determine effective orientation based on mobile config
  const effectiveOrientation = useMemo(() => {
    if (isMobile && mobile.verticalOrientation !== false) {
      return "vertical";
    }
    return orientation;
  }, [isMobile, mobile.verticalOrientation, orientation]);

  // Resolve pagination configuration for each list
  const leftPaginationConfig = useMemo(() => resolvePaginationConfig(pagination, "left"), [pagination]);
  const rightPaginationConfig = useMemo(() => resolvePaginationConfig(pagination, "right"), [pagination]);

  // Store refs to pagination hooks for imperative control
  const leftPaginationRef = useRef<ReturnType<typeof useTransferListPagination> | null>(null);
  const rightPaginationRef = useRef<ReturnType<typeof useTransferListPagination> | null>(null);

  // Store initial data for reset functionality
  const [storedInitialData] = useState<TransferListData<T>>(() => {
    // Use initialData if provided, otherwise use default empty lists
    if (initialData) {
      return initialData;
    }
    // Fallback to hardcoded data for backward compatibility (will be removed in future tasks)
    return {
      left: [] as T[],
      right: [] as T[]
    };
  });

  // Internal state for uncontrolled mode
  const [internalData, setInternalData] = useState<TransferListData<T>>(storedInitialData);

  // Detect if component is in controlled mode
  const isControlled = value !== undefined;

  // Effective data: use value prop in controlled mode, internal state in uncontrolled mode
  const effectiveData = isControlled ? value : internalData;

  // Function to update data - handles both controlled and uncontrolled modes
  const updateData = (newData: TransferListData<T>) => {
    // Update internal state only in uncontrolled mode
    if (!isControlled) {
      setInternalData(newData);
    }

    // Always call onChange callback if provided (for both modes)
    onChange?.(newData);
  };

  const [leftSelection, setLeftSelection] = useState<(string | number)[]>([]);
  const [rightSelection, setRightSelection] = useState<(string | number)[]>([]);

  // Default item handlers for strings (will be replaced with props in future tasks)
  const getItemLabel = customGetItemLabel || ((item: T) => String(item));
  const getItemValue = customGetItemValue || ((item: T) => String(item));

  // Merge custom labels with defaults
  const effectiveLabels = {
    ...defaultLabels,
    ...labels
  };

  // Calculate if transfer is allowed for each list based on maxItems
  const canTransferToRight = maxItems?.right === undefined || effectiveData.right.length < maxItems.right;
  const canTransferToLeft = maxItems?.left === undefined || effectiveData.left.length < maxItems.left;

  /**
   * Handle transfer of items between lists with full validation
   * @param from - Source list direction ("left" or "right")
   * @param itemsToTransfer - Items to be transferred
   */
  const handleTransfer = async (from: "left" | "right", itemsToTransfer: T[]) => {
    // Determine target direction
    const to = from === "left" ? "right" : "left";

    // Call onBeforeTransfer validation if provided
    if (onBeforeTransfer) {
      try {
        const canTransfer = await onBeforeTransfer({
          from,
          to,
          items: itemsToTransfer
        });

        // If validation fails, cancel the transfer
        if (!canTransfer) {
          return;
        }
      } catch (error) {
        // If onBeforeTransfer throws an error, cancel the transfer
        console.warn("Transfer validation failed:", error);
        return;
      }
    }

    // Get current lists
    const sourceList = effectiveData[from];
    const targetList = effectiveData[to];

    // Check for duplicates if allowDuplicates is false (default)
    if (!allowDuplicates) {
      const targetValues = targetList.map(getItemValue);
      const duplicates = itemsToTransfer.filter((item) => targetValues.includes(getItemValue(item)));

      if (duplicates.length > 0) {
        // Call custom duplicate handler if provided
        if (onDuplicateDetected) {
          onDuplicateDetected(duplicates, to);
        } else {
          // Default behavior: show toast
          const duplicateCount = duplicates.length;
          const message = duplicateCount === 1 ? "Item duplicado não é permitido" : `${duplicateCount} itens duplicados não são permitidos`;
          toast.error(message);
        }
        return;
      }
    }

    // Validate maxItems constraint for target list
    const maxItemsForTarget = maxItems?.[to];
    if (maxItemsForTarget !== undefined && targetList.length + itemsToTransfer.length > maxItemsForTarget) {
      // Transfer would exceed maximum items allowed in target list
      console.warn(`Transfer blocked: would exceed maximum of ${maxItemsForTarget} items in ${to} list`);
      return;
    }

    // Get values of items to transfer for filtering
    const transferredValues = itemsToTransfer.map(getItemValue);

    // Remove transferred items from source list
    const newSourceList = sourceList.filter((item) => !transferredValues.includes(getItemValue(item)));

    // Add transferred items to target list
    const newTargetList = [...targetList, ...itemsToTransfer];

    // Create new data object with updated lists
    const newData: TransferListData<T> = {
      left: from === "left" ? newSourceList : newTargetList,
      right: from === "right" ? newSourceList : newTargetList
    };

    // Update data using the unified update function
    updateData(newData);

    // Call onTransfer callback with all required parameters
    onTransfer?.({
      from,
      to,
      items: itemsToTransfer,
      leftData: newData.left,
      rightData: newData.right
    });
  };

  /**
   * Handle transfer of all filtered items from one list to another
   * This function reuses all validations from handleTransfer and calls onTransferAll callback
   * @param from - Source list direction ("left" or "right")
   * @param filteredItems - Filtered items to be transferred (from RenderList)
   */
  const handleTransferAll = async (from: "left" | "right", filteredItems: T[]) => {
    // Determine target direction
    const to = from === "left" ? "right" : "left";

    // Call onBeforeTransfer validation if provided (reusing validation logic)
    if (onBeforeTransfer) {
      try {
        const canTransfer = await onBeforeTransfer({
          from,
          to,
          items: filteredItems
        });

        // If validation fails, cancel the transfer
        if (!canTransfer) {
          return;
        }
      } catch (error) {
        // If onBeforeTransfer throws an error, cancel the transfer
        console.warn("Transfer all validation failed:", error);
        return;
      }
    }

    // Get current lists
    const sourceList = effectiveData[from];
    const targetList = effectiveData[to];

    // Check for duplicates if allowDuplicates is false (default)
    if (!allowDuplicates) {
      const targetValues = targetList.map(getItemValue);
      const duplicates = filteredItems.filter((item) => targetValues.includes(getItemValue(item)));

      if (duplicates.length > 0) {
        // Call custom duplicate handler if provided
        if (onDuplicateDetected) {
          onDuplicateDetected(duplicates, to);
        } else {
          // Default behavior: show toast
          const duplicateCount = duplicates.length;
          const message = duplicateCount === 1 ? "Item duplicado não é permitido" : `${duplicateCount} itens duplicados não são permitidos`;
          toast.error(message);
        }
        return;
      }
    }

    // Validate maxItems constraint for target list (reusing validation logic)
    const maxItemsForTarget = maxItems?.[to];
    if (maxItemsForTarget !== undefined && targetList.length + filteredItems.length > maxItemsForTarget) {
      // Transfer would exceed maximum items allowed in target list
      console.warn(`Transfer all blocked: would exceed maximum of ${maxItemsForTarget} items in ${to} list`);
      return;
    }

    // Get values of items to transfer for filtering
    const transferredValues = filteredItems.map(getItemValue);

    // Remove transferred items from source list
    const newSourceList = sourceList.filter((item) => !transferredValues.includes(getItemValue(item)));

    // Add transferred items to target list
    const newTargetList = [...targetList, ...filteredItems];

    // Create new data object with updated lists
    const newData: TransferListData<T> = {
      left: from === "left" ? newSourceList : newTargetList,
      right: from === "right" ? newSourceList : newTargetList
    };

    // Update data using the unified update function
    updateData(newData);

    // Call onTransfer callback with all required parameters (for consistency)
    onTransfer?.({
      from,
      to,
      items: filteredItems,
      leftData: newData.left,
      rightData: newData.right
    });

    // Call onTransferAll callback after successful transfer
    onTransferAll?.({
      from,
      to,
      items: filteredItems
    });

    // Clear selection after transferring all items
    if (from === "left") {
      setLeftSelection([]);
    } else {
      setRightSelection([]);
    }
  };

  // Expose imperative methods via ref
  useImperativeHandle(
    ref,
    () => ({
      /**
       * Transfer all items from one list to another programmatically
       * @param direction - Direction of transfer ("left-to-right" or "right-to-left")
       */
      transferAll: (direction: "left-to-right" | "right-to-left") => {
        const from = direction === "left-to-right" ? "left" : "right";
        const items = effectiveData[from];
        handleTransferAll(from, items);
      },

      /**
       * Clear all current selections in both lists
       */
      clearSelection: () => {
        setLeftSelection([]);
        setRightSelection([]);
      },

      /**
       * Reset the component to its initial state
       * This restores the data to initialData and clears all selections
       */
      reset: () => {
        // Reset data to initial state
        if (!isControlled) {
          setInternalData(storedInitialData);
        }
        // Call onChange with initial data (for controlled mode)
        onChange?.(storedInitialData);

        // Clear selections
        setLeftSelection([]);
        setRightSelection([]);
      },

      /**
       * Get the current state of both lists
       * @returns Current TransferListData with left and right lists
       */
      getState: () => {
        return effectiveData;
      },

      /**
       * Navigate to a specific page in a list
       * @param list - Which list to navigate ("left" or "right")
       * @param page - Page number (1-indexed)
       */
      goToPage: (list: "left" | "right", page: number) => {
        const paginationRef = list === "left" ? leftPaginationRef : rightPaginationRef;
        if (paginationRef.current) {
          paginationRef.current.setPage(page);
        }
      },

      /**
       * Change page size programmatically
       * @param list - Which list to update ("left" or "right")
       * @param size - New page size
       */
      setPageSize: (list: "left" | "right", size: number) => {
        const paginationRef = list === "left" ? leftPaginationRef : rightPaginationRef;
        if (paginationRef.current) {
          paginationRef.current.changePageSize(size);
        }
      },

      /**
       * Get current pagination state for both lists
       * @returns Pagination state for left and right lists (null if pagination is disabled)
       */
      getPaginationState: () => {
        return {
          left: leftPaginationRef.current
            ? {
                currentPage: leftPaginationRef.current.active,
                pageSize: leftPaginationRef.current.pageSize,
                totalItems: leftPaginationRef.current.totalItems,
                totalPages: leftPaginationRef.current.totalPages
              }
            : null,
          right: rightPaginationRef.current
            ? {
                currentPage: rightPaginationRef.current.active,
                pageSize: rightPaginationRef.current.pageSize,
                totalItems: rightPaginationRef.current.totalItems,
                totalPages: rightPaginationRef.current.totalPages
              }
            : null
        };
      }
    }),
    [effectiveData, isControlled, storedInitialData, onChange, handleTransferAll]
  );

  const styles = {
    title: cn(classes.title, classNames?.title)
  };

  return (
    <div className={cn(classes.root, classNames?.root, className)} data-orientation={effectiveOrientation} data-size={size} onBlur={onBlur}>
      {effectiveOrientation === "horizontal" && (leftTitle || rightTitle) && (
        <div className={cn(classes.titles, classNames?.titles)}>
          <Text className={styles.title}>{leftTitle}</Text>
          <Text className={styles.title}>{rightTitle}</Text>
        </div>
      )}
      <div className={cn(classes.listsContainer, classNames?.listsContainer)}>
        <div className={cn(classes.listWrapper, classNames?.listWrapper)}>
          {effectiveOrientation === "vertical" && leftTitle && <Text className={styles.title}>{leftTitle}</Text>}
          <RenderList
            type="forward"
            options={effectiveData.left}
            selectedValues={leftSelection}
            onSelectionChange={setLeftSelection}
            onTransfer={(options) => handleTransfer("left", options)}
            onTransferAll={(filteredItems) => handleTransferAll("left", filteredItems)}
            getItemLabel={getItemLabel}
            getItemValue={getItemValue}
            searchable={searchable}
            showTransferAll={showTransferAll}
            disabled={disabled}
            canTransfer={canTransferToRight}
            filter={filter}
            searchPlaceholder={effectiveLabels.searchPlaceholder}
            emptyMessage={effectiveLabels.emptyList}
            nothingFoundMessage={effectiveLabels.nothingFound}
            height={height}
            variant={variant}
            columns={columns}
            classNames={classNames}
            orientation={effectiveOrientation}
            size={size}
            loading={loading}
            loaderProps={loaderProps}
            paginationConfig={leftPaginationConfig}
            listSide="left"
            paginationRef={leftPaginationRef}
            tooltipConfig={tooltips}
            color={color}
          />
        </div>
        <div className={cn(classes.listWrapper, classNames?.listWrapper)}>
          {effectiveOrientation === "vertical" && rightTitle && <Text className={styles.title}>{rightTitle}</Text>}
          <RenderList
            type="backward"
            options={effectiveData.right}
            selectedValues={rightSelection}
            onSelectionChange={setRightSelection}
            onTransfer={(options) => handleTransfer("right", options)}
            onTransferAll={(filteredItems) => handleTransferAll("right", filteredItems)}
            getItemLabel={getItemLabel}
            getItemValue={getItemValue}
            searchable={searchable}
            showTransferAll={showTransferAll}
            disabled={disabled}
            canTransfer={canTransferToLeft}
            filter={filter}
            searchPlaceholder={effectiveLabels.searchPlaceholder}
            emptyMessage={effectiveLabels.emptyList}
            nothingFoundMessage={effectiveLabels.nothingFound}
            height={height}
            variant={variant}
            columns={columns}
            classNames={classNames}
            orientation={effectiveOrientation}
            size={size}
            loading={loading}
            loaderProps={loaderProps}
            paginationConfig={rightPaginationConfig}
            listSide="right"
            paginationRef={rightPaginationRef}
            tooltipConfig={tooltips}
            color={color}
          />
        </div>
      </div>
      {error && <Text className={cn(classes.error, classNames?.error)}>{error}</Text>}
    </div>
  );
}

/**
 * InputTransferList - A flexible transfer list component with ref support
 *
 * Allows transferring items between two lists with search, validation, and imperative control.
 * Supports both controlled and uncontrolled modes.
 *
 * @template T - The type of items in the lists
 *
 * @example
 * // Basic usage with strings
 * const ref = useRef<TransferListRef>(null);
 * <InputTransferList
 *   ref={ref}
 *   initialData={{ left: ['Item 1', 'Item 2'], right: [] }}
 *   onChange={(data) => console.log(data)}
 * />
 *
 * @example
 * // Using ref methods
 * ref.current?.transferAll('left-to-right');
 * ref.current?.clearSelection();
 * ref.current?.reset();
 * const state = ref.current?.getState();
 */
export const InputTransferList = forwardRef(InputTransferListInner) as <T = TransferListItem>(
  props: InputTransferListProps<T> & { ref?: React.Ref<TransferListRef<T>> }
) => ReturnType<typeof InputTransferListInner>;
